import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Clipboard, RefreshCw, Save, Sparkles, Lock } from 'lucide-react';
import MarkdownRenderer from '@/components/MarkdownRenderer';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import TemplateForm from '@/components/communication/TemplateForm';
import { saveTemplate } from '@/services/communication/templates';
import CustomPromptDialog from '@/components/communication/CustomPromptDialog';
import { canAccessCallScripts } from '@/services/subscriptions/access';
import { UpgradePrompt } from '@/components/subscription/UpgradePrompt';

interface CallScriptViewProps {
  callScript: string;
  isLoading: boolean;
  onGenerate: () => Promise<void>;
  onGenerateWithPrompt: (promptText: string) => Promise<void>;
  leadStatus?: string;
  compact?: boolean;
}

const CallScriptView: React.FC<CallScriptViewProps> = ({
  callScript,
  isLoading,
  onGenerate,
  onGenerateWithPrompt,
  leadStatus,
  compact = false
}) => {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [isCustomPromptOpen, setIsCustomPromptOpen] = useState(false);
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    const access = await canAccessCallScripts();
    setHasAccess(access);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Call Script Copied',
      description: "Text has been copied to your clipboard."
    });
  };

  const handleSaveTemplate = async (templateData: any) => {
    setIsSaving(true);
    try {
      const result = await saveTemplate({
        ...templateData,
        type: 'call',
      });
      
      if (result) {
        toast({
          title: "Call Script Saved",
          description: "Your script has been saved successfully."
        });
        setIsSaveDialogOpen(false);
      } else {
        throw new Error("Failed to save script");
      }
    } catch (error) {
      console.error('Error saving script:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save call script."
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCustomGeneration = (promptText: string) => {
    setIsCustomPromptOpen(false);
    onGenerateWithPrompt(promptText);
  };

  if (hasAccess === null) {
    return <div className="p-8 text-center text-muted-foreground text-xs">Loading...</div>;
  }

  if (!hasAccess) {
    return (
      <UpgradePrompt
        feature="Call Scripts Available on Starter Plan"
        description="Upgrade to Starter or higher to access AI-generated call script templates."
        tier="starter"
        icon={<Lock className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />}
        benefits={[
          'AI-generated personalized call scripts',
          'Save and reuse custom call templates',
          'Customize scripts for different lead statuses',
          'Access to 100 leads per month'
        ]}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className={`font-semibold ${compact ? 'text-base' : 'text-lg'}`}>Call Script</h3>
      </div>

      <div className="flex gap-2 items-center flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsCustomPromptOpen(true)}
          disabled={isLoading}
          className="h-9"
        >
          <Sparkles className="h-4 w-4 mr-2" />
          Customize
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onGenerate}
          disabled={isLoading}
          className="h-9"
        >
          {isLoading ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              {callScript ? 'Regenerate' : 'Generate'}
            </>
          )}
        </Button>
        {callScript && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyToClipboard(callScript)}
              className="h-9"
            >
              <Clipboard className="h-4 w-4 mr-2" />
              Copy
            </Button>
            <Dialog open={isSaveDialogOpen} onOpenChange={setIsSaveDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[700px]">
                <TemplateForm
                  type="call"
                  template={{
                    content: callScript,
                    leadStatus: leadStatus,
                    name: `Call script for ${leadStatus || 'lead'}`
                  }}
                  onSave={handleSaveTemplate}
                  onCancel={() => setIsSaveDialogOpen(false)}
                />
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>

      {callScript ? (
        <div className={`bg-muted/50 p-6 rounded-md overflow-auto ${compact ? 'max-h-[300px]' : 'max-h-[500px]'}`}>
          <MarkdownRenderer content={callScript} />
        </div>
      ) : (
        <div className="bg-muted/30 p-8 rounded-md text-center">
          <p className="text-muted-foreground">
            No call script generated yet. Click Generate to create a personalized call script.
          </p>
        </div>
      )}

      <CustomPromptDialog
        isOpen={isCustomPromptOpen}
        onClose={() => setIsCustomPromptOpen(false)}
        onSubmit={handleCustomGeneration}
        type="call"
        isLoading={isLoading}
      />
    </div>
  );
};

export default CallScriptView;
